// RepCounter class for tracking exercise repetitions and form quality
export class RepCounter {
  private _repCount: number = 0;
  private inRep: boolean = false;
  private repScores: Array<{ rep: number; score: number; details: string[] }> = [];
  private currentRepScore: number = 100;
  private currentRepDetails: string[] = [];
  private kneeAngleHistory: number[] = [];
  private hipAngleHistory: number[] = [];
  private backAngleHistory: number[] = [];
  private repStartTime: number = 0;
  private lastProcessTime: number = 0;
  
  // Thresholds for different exercises
  private readonly SQUAT_KNEE_THRESHOLD = 130; // Degrees
  private readonly DEADLIFT_HIP_THRESHOLD = 120; // Degrees
  
  constructor() {
    this.resetCounter();
  }
  
  resetCounter() {
    this._repCount = 0;
    this.inRep = false;
    this.repScores = [];
    this.currentRepScore = 100;
    this.currentRepDetails = [];
    this.kneeAngleHistory = [];
    this.hipAngleHistory = [];
    this.backAngleHistory = [];
    this.repStartTime = 0;
    this.lastProcessTime = 0;
  }
  
  // Process angles and determine if in a rep
  processAngles(kneeAngle: number, hipAngle: number, backAngle: number): boolean {
    // Store angles in history
    this?.kneeAngleHistory?.push(kneeAngle);
    this?.hipAngleHistory?.push(hipAngle);
    this?.backAngleHistory?.push(backAngle);
    
    // Keep history at a reasonable size
    if (this?.kneeAngleHistory?.length > 30) {
      this?.kneeAngleHistory?.shift();
      this?.hipAngleHistory?.shift();
      this?.backAngleHistory?.shift();
    }
    
    const now = Date.now();
    
    // Throttle processing to avoid rapid fluctuations
    if (this.lastProcessTime && now - this.lastProcessTime < 100) {
      return this.inRep;
    }
    
    this.lastProcessTime = now;
    
    // Detect rep based on knee angle (for squats)
    const wasInRep = this.inRep;
    this.inRep = kneeAngle < this.SQUAT_KNEE_THRESHOLD;
    
    // Rep transition detection
    if (!wasInRep && this.inRep) {
      // Starting a new rep
      this.repStartTime = now;
      this.currentRepScore = 100;
      this.currentRepDetails = [];
      
      // Check starting position
      this.checkStartingPosition(kneeAngle, hipAngle, backAngle);
    } else if (wasInRep && !this.inRep) {
      // Finishing a rep
      this._repCount++;
      
      // Check rep duration
      const repDuration = now - this.repStartTime;
      this.checkRepDuration(repDuration);
      
      // Check form during the rep
      this.checkFormDuringRep();
      
      // Save rep score
      this?.repScores?.push({
        rep: this._repCount,
        score: this.currentRepScore,
        details: this.currentRepDetails
      });
    } else if (this.inRep) {
      // During rep - check form
      this.checkFormDuringExecution(kneeAngle, hipAngle, backAngle);
    }
    
    return this.inRep;
  }
  
  private checkStartingPosition(kneeAngle: number, hipAngle: number, backAngle: number) {
    // Check if starting position is correct
    if (kneeAngle < 160) {
      this.deductPoints(5, "Starting position: knees should be more extended");
    }
    
    if (hipAngle < 160) {
      this.deductPoints(5, "Starting position: hips should be more extended");
    }
    
    if (backAngle < 80 || backAngle > 100) {
      this.deductPoints(10, "Starting position: back should be more upright");
    }
  }
  
  private checkRepDuration(duration: number) {
    // Check if rep was too fast or too slow
    if (duration < 1000) {
      this.deductPoints(15, "Rep was too fast (less than 1 second)");
    } else if (duration > 5000) {
      this.deductPoints(10, "Rep was too slow (more than 5 seconds)");
    }
  }
  
  private checkFormDuringRep() {
    // Check for consistency in movement
    const kneeVariance = this.calculateVariance(this.kneeAngleHistory);
    if (kneeVariance > 200) {
      this.deductPoints(10, "Inconsistent knee movement during rep");
    }
    
    const hipVariance = this.calculateVariance(this.hipAngleHistory);
    if (hipVariance > 200) {
      this.deductPoints(10, "Inconsistent hip movement during rep");
    }
    
    const backVariance = this.calculateVariance(this.backAngleHistory);
    if (backVariance > 100) {
      this.deductPoints(15, "Back position was not stable during rep");
    }
  }
  
  private checkFormDuringExecution(kneeAngle: number, hipAngle: number, backAngle: number) {
    // Check for common form issues during the rep
    
    // For squats - check if knees are tracking over toes
    if (kneeAngle < 90) {
      this.deductPoints(5, "Knees bent too much (below 90 degrees)");
    }
    
    // Check if back is rounded
    if (backAngle < 70 || backAngle > 110) {
      this.deductPoints(1, "Back not maintained in neutral position");
    }
    
    // Check if hips are dropping too low
    if (hipAngle < 70) {
      this.deductPoints(1, "Hips dropping too low");
    }
  }
  
  private deductPoints(points: number, reason: string) {
    // Don't add duplicate reasons
    if (!this?.currentRepDetails?.includes(reason)) {
      this.currentRepScore = Math.max(0, this.currentRepScore - points);
      this?.currentRepDetails?.push(reason);
    }
  }
  
  private calculateVariance(values: number[]): number {
    if (values.length === 0) return 0;
    
    const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
    const squaredDiffs = Array.isArray(values) ? values.map(val => Math.pow(val - mean, 2)) : [];
    const variance = squaredDiffs.reduce((sum, val) => sum + val, 0) / values.length;
    
    return variance;
  }
  
  // Getter for rep count
  get repCount(): number {
    return this._repCount;
  }
  
  getAverageScore(): number {
    if (this?.repScores?.length === 0) return 0;
    const sum = this?.repScores?.reduce((total, rep) => total + rep.score, 0);
    return sum / this?.repScores?.length;
  }
  
  getFormattedRepScores() {
    return this.repScores;
  }
}